// Capacitor delivers Universal Links (iOS) and App Links (Android) as an
// `appUrlOpen` event, NOT by navigating the WebView. The native shell always
// boots at `server.url` -- the bare /passes/ origin -- so the ?code= on the
// tapped link never reaches the page on its own. Without this handler the app
// opens on an empty login form and the member has to type their order number.
//
// We reach the App plugin through the global Capacitor bridge rather than
// depending on @capacitor/app: this bundle is shared with the browser PWA, and
// the two shells sit on different Capacitor majors (iOS 5, Android 8), so a
// package dependency would pin the web app to one of them.

type AppPlugin = {
  addListener?: (event: 'appUrlOpen', cb: (data: { url: string }) => void) => unknown;
  getLaunchUrl?: () => Promise<{ url: string } | null>;
};

const PASSES_PATH = '/passes';
const LAUNCH_HANDLED_KEY = 'monkeybook-launch-url-handled';

let registered = false;

const applyDeepLink = (rawUrl: string) => {
  let url: URL;
  try {
    url = new URL(rawUrl);
  } catch {
    return;
  }

  if (url.pathname !== PASSES_PATH && !url.pathname.startsWith(`${PASSES_PATH}/`)) return;

  const code = url.searchParams.get('code');
  if (!code) return;

  // Rebuild the query from known params only. Pass links carry a cosmetic `n`
  // (the member's name, so whoever copies the link can tell whose it is) that
  // nothing reads -- drop it rather than carry it into the app's URL.
  const params = new URLSearchParams({ code });
  const language = url.searchParams.get('language');
  if (language) params.set('language', language);

  const target = `${PASSES_PATH}?${params.toString()}`;
  if (`${window.location.pathname}${window.location.search}` === target) return;

  // A full navigation rather than a router push: it re-enters through
  // PassLogin's mount effect, which already owns code -> lookup -> login.
  window.location.replace(target);
};

export const registerNativeDeepLink = () => {
  if (registered) return;
  if (typeof window === 'undefined') return;

  const app = (window as unknown as { Capacitor?: { Plugins?: { App?: AppPlugin } } }).Capacitor?.Plugins?.App;
  // Browser PWA: no bridge, nothing to listen to.
  if (!app?.addListener) return;

  registered = true;

  app.addListener('appUrlOpen', ({ url }) => applyDeepLink(url));

  // Cold start: the launch URL can be delivered before this listener exists,
  // so ask for it directly as well.
  //
  // It has to be consumed exactly once. getLaunchUrl keeps returning the same
  // URL for the life of the process, so after the code resolves and PassLogin
  // navigates to the dashboard, an unguarded second call sees a location that
  // no longer matches and bounces straight back to /passes?code= -- an endless
  // reload loop that shows as a spinner that never resolves. sessionStorage is
  // the right scope: it survives the location.replace below but not a relaunch,
  // so a genuine cold start with the same link still works.
  void app
    .getLaunchUrl?.()
    .then((launch) => {
      if (!launch?.url) return;
      try {
        if (window.sessionStorage.getItem(LAUNCH_HANDLED_KEY) === launch.url) return;
        window.sessionStorage.setItem(LAUNCH_HANDLED_KEY, launch.url);
      } catch {
        // sessionStorage unavailable -- fall through; the location check in
        // applyDeepLink still prevents an immediate self-replace.
      }
      applyDeepLink(launch.url);
    })
    .catch(() => undefined);
};
